import { useQuery } from "@apollo/client";
import { useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";

import useAuth from "@/hooks/use-auth";
import type { ExploreSearchParams } from "@/routes/explore/index";

import { GET_GAMES } from "../graphql";
import apiVariables from "../helpers/api-variables";
import {
  type DataType,
  GameStatusFilterEnum,
  IncludeEnum,
  type VariablesType,
} from "../types";

// Hook to detect closed games for current search and manage hint state
export default function useClosedGamesHint(
  currentPage = 1,
  hasMore = true,
  hasResults = true
) {
  const search = useSearch({ strict: false }) as ExploreSearchParams;
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { queryContext, isUser } = useAuth();
  const [isDismissed, setIsDismissed] = useState(false);

  const searchQuery = search.query;
  const statusFilter = search.status;
  const hasClosedGamesFilter =
    statusFilter === GameStatusFilterEnum.PERMANENTLY_CLOSED;

  // Check if we should query for closed games
  const shouldQueryClosedGames = !!searchQuery && !hasClosedGamesFilter;

  // Build variables for checking if closed games exist
  const closedGamesVariables = useMemo<VariablesType>(() => {
    const baseVars = apiVariables(search, { isUser, pageSize: 1 });

    // Apply user-specific include filters (same as use-search-filters.ts)
    if (isUser && search.include) {
      if (search.include === IncludeEnum.NOT_PLAYED) {
        baseVars.excludePlayed = true;
      }
      if (search.include === IncludeEnum.PLAYED) {
        baseVars.escapedOnly = true;
      }
      if (search.include === IncludeEnum.SAVED) {
        baseVars.savedOnly = true;
      }
    }

    return {
      ...baseVars,
      filters: {
        ...baseVars.filters,
        status: [GameStatusFilterEnum.PERMANENTLY_CLOSED],
      },
      pageSize: 1,
    };
  }, [search, isUser]);

  // Query to check if closed games exist
  const { data, loading } = useQuery<DataType, VariablesType>(GET_GAMES, {
    ...queryContext(closedGamesVariables),
    fetchPolicy: "cache-first",
    skip: !shouldQueryClosedGames,
  });

  const closedGamesCount = data?.games?.count ?? 0;
  const hasClosedGames = closedGamesCount > 0;

  // Show hint banner only when there are results (empty state handles 0-result case)
  const shouldShowHint =
    !!searchQuery &&
    hasClosedGames &&
    !hasClosedGamesFilter &&
    !isDismissed &&
    !loading &&
    hasResults &&
    (currentPage >= 2 || !hasMore);

  const dismiss = useCallback(() => {
    setIsDismissed(true);
  }, []);

  // Navigate to show closed games (used by both hint banner and empty state)
  const showClosedGames = useCallback(() => {
    navigate({
      search: {
        ...search,
        status: GameStatusFilterEnum.PERMANENTLY_CLOSED,
      },
      to: pathname,
    });
    dismiss();
  }, [pathname, search, navigate, dismiss]);

  // Auto-remove closed filter when search query is cleared (iOS parity)
  useEffect(() => {
    if (!searchQuery && hasClosedGamesFilter) {
      const { status: _status, ...rest } = search;
      navigate({ search: rest, to: pathname });
    }
  }, [searchQuery, hasClosedGamesFilter, pathname, navigate, search]);

  // Reset dismissed state when search query changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset state on query change
  useEffect(() => {
    setIsDismissed(false);
  }, [searchQuery]);

  return {
    closedGamesCount,
    dismiss,
    hasClosedGames,
    hasClosedGamesFilter,
    isLoading: loading,
    shouldShowHint,
    showClosedGames,
  };
}
